import { SpawnType } from "@1games-ru/region-shared/dist/enums/selectSpawn/type";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import SpawnMpProvider from "@/core/data-provider/data-mp/spawn/spawn-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector.ts";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import {
  activeSpawnTypeSelector,
  hoverSpawnTypeIndexSelector,
  hoverSpawnTypeSelector,
  setActiveSpawnType,
  setHoverSpawnType,
  spawnTypesSelector,
} from "@/domains/spawn/slice.ts";
import toast from "@/domains/toasts/helpers/toast.ts";

export default () => {
  const { t } = useTranslation("spawn");
  const hoverSpawnType = useAppSelector(hoverSpawnTypeSelector);
  const activeSpawnType = useAppSelector(activeSpawnTypeSelector);
  const hoverSpawnTypeIndex = useAppSelector(hoverSpawnTypeIndexSelector);
  const spawnTypes = useAppSelector(spawnTypesSelector);
  const dispatch = useAppDispatch();

  const setActiveSpawnHandler = useCallback(
    async (spawnType: SpawnType) => {
      try {
        const response = await SpawnMpProvider.setActiveSpawnType(spawnType);
        if (response instanceof RpcMpProviderFailedResponse) {
          console.log({ response });
          toast.error(t("spawn.errors.SET_ACTIVE_SPAWN_TYPE_ERROR"));
          return;
        }
        dispatch(setActiveSpawnType(spawnType));
      } catch (error) {
        console.error(error);
        toast.error(t("spawn.errors.SET_ACTIVE_SPAWN_TYPE_ERROR"));
      }
    },
    [dispatch, t],
  );

  const setHoverSpawnHandler = useCallback(
    async (spawnType: SpawnType) => {
      try {
        const response = await SpawnMpProvider.setHoverSpawnType(spawnType);
        if (response instanceof RpcMpProviderFailedResponse) {
          console.log({ response });
          toast.error(t("spawn.errors.SET_HOVER_SPAWN_TYPE_ERROR"));
          return;
        }
        dispatch(setHoverSpawnType(spawnType));
      } catch (error) {
        console.error(error);
        toast.error(t("spawn.errors.SET_HOVER_SPAWN_TYPE_ERROR"));
      }
    },
    [dispatch, t],
  );

  const confirmSpawnHandler = useCallback(async () => {
    if (
      activeSpawnType === SpawnType.Undefined ||
      (hoverSpawnType !== SpawnType.Undefined && activeSpawnType !== hoverSpawnType)
    ) {
      await setActiveSpawnHandler(hoverSpawnType);
      return;
    }
    try {
      const response = await SpawnMpProvider.confirmSpawnType();
      if (response instanceof RpcMpProviderFailedResponse) {
        console.log({ response });
        toast.error(t("spawn.errors.CONFIRM_SPAWN_TYPE_ERROR"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("spawn.errors.CONFIRM_SPAWN_TYPE_ERROR"));
    }
  }, [activeSpawnType, hoverSpawnType, setActiveSpawnHandler, t]);

  const nextHoverSpawnHandler = useCallback(async () => {
    if (spawnTypes.length === 0) {
      return;
    }
    const nextHoverSpawnType =
      spawnTypes[hoverSpawnTypeIndex + 1] !== undefined
        ? spawnTypes[hoverSpawnTypeIndex + 1]
        : spawnTypes[0];

    await setHoverSpawnHandler(nextHoverSpawnType);
  }, [hoverSpawnTypeIndex, spawnTypes, setHoverSpawnHandler]);

  const prevHoverSpawnHandler = useCallback(async () => {
    if (spawnTypes.length === 0) {
      return;
    }

    const prevHoverSpawnType =
      spawnTypes[hoverSpawnTypeIndex - 1] !== undefined
        ? spawnTypes[hoverSpawnTypeIndex - 1]
        : spawnTypes[spawnTypes.length - 1];

    await setHoverSpawnHandler(prevHoverSpawnType);
  }, [hoverSpawnTypeIndex, spawnTypes, setHoverSpawnHandler]);

  return {
    setActiveSpawnHandler,
    setHoverSpawnHandler,
    confirmSpawnHandler,
    nextHoverSpawnHandler,
    prevHoverSpawnHandler,
  };
};
